<script>
import Layout from '../../layouts/main'
import PageHeader from '@/components/page-header'
import Loader from '@/components/widgets/loader'
import { apiService } from '@/helpers/api.service';
import Swal from 'sweetalert2';
import { permissionsMap, checkPermission } from '@/utils/utilsPermissions';


export default {
    components: { Layout, PageHeader, Loader },
    props: {},
    data() {
        return {
            loading: false,
            showModal: false,
            isSendData: false,
            selectedUser: { name: '', email: '', status: '', password: '', roleId: '' },
            errors: { name: '', email: '', password: '', status: '', roleId: '' },
            showPassword: false,
            emailRegex: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            modalStyle: {},
            isEditMode: false,
            isDelete: false,
            showSuccess: false,
            userToDelete: null,
            roles: [],
            users: [],
        };
    },
    computed: {
        isAdminUser() {
            if (!this.isEditMode) return false;
            if (!this.selectedUser.roleId) return false;
            const userRole = this.roles.find(r => r.id === this.selectedUser.roleId);
            return userRole?.name === 'Administrador';
        }
    },
    mounted() {
        this.getRoles()
        this.getPermission()
        this.canEditUser()
        this.canCreateUser()
    },
    methods: {
        canEditUser() {
            return checkPermission(permissionsMap.UPDATE_STORE_USER, this.$store.state.permissions);
        },
        canDeleteUser(user) {
            const hasPermission = checkPermission(permissionsMap.DELETE_STORE_USER, this.$store.state.permissions);
            if (!hasPermission) return false;
            const userRole = this.roles.find(r => r.id === (user.role_id?._id || user.role_id));
            return userRole?.name !== 'Administrador';
        },
        canCreateUser() {
            return checkPermission(permissionsMap.CREATE_STORE_USER, this.$store.state.permissions);
        },
        enumStatus(status) {
            const enumStatus = {
                Active: 'Activo',
                Pending: 'Pendiente',
                Suspended: 'Suspendido'
            }
            return enumStatus[status] || ''
        },
        description(idx) {
            const descriptions = [
                'Poder de gestionar usuarios, modificar configuraciones, acceder a informes y realizar copias de seguridad. También asigna roles y supervisa la actividad en la plataforma.',
                'Un agente limitado puede gestionar usuarios, modificar configuraciones, acceder a informes básicos y realizar copias de seguridad. Su rol incluye asignar tareas y supervisar la actividad en la plataforma.',
                'Limitado a gestionar usuarios, modificar configuraciones, acceder a informes básicos y realizar copias de seguridad. Su rol incluye asignar tareas y supervisar la actividad en la plataforma.',
            ];
            return descriptions[idx] || '';
        },
        getPermission() {
            this.loading = true
            apiService.getPermissions().then(response => {
                const data = response.data
                this.users = data
            }) .catch(error => {
                console.error('Error al obtener roles:', error);
                this.users = [];
            }).finally(() => {
                this.loading = false
            })
        },
        getRoles() {
            apiService.getRoles().then(response => {
                const data = response.data
                this.roles = data.map(role => ({ 
                    id: role._id, 
                    name: role.name 
                }));
            }) .catch(error => {
                console.error('Error al obtener roles:', error);
                this.roles = [];
            });
        },
        openModal(user = null, event) {
            if (user) {
                this.selectedUser = { 
                    ...user, 
                    roleId: user.role_id?._id || '',
                    id: user._id,
                    password: ''
                };
                this.isEditMode = true;
            } else {
                this.selectedUser = { name: '', email: '', roleId: '', status: '', password: '' };
                this.isEditMode = false;
            }
            window.scrollTo(0, 0);
            const { top, left } = event.target.getBoundingClientRect();
            this.modalStyle = { top: `${top + window.scrollY}px`, left: `${left}px` };
            this.showModal = true;
        },
        closeModal() {
            this.showModal = false;
        },
        validateField(field) {
            this.$set(this.errors, field, '');
            
            if (field === 'name') {
                if (!this.selectedUser.name) {
                    this.errors.name = 'El nombre es requerido';
                } else if (this.selectedUser.name.length < 3) {
                    this.errors.name = 'El nombre debe tener al menos 3 caracteres';
                }
            } else if (field === 'email') {
                if (!this.selectedUser.email) {
                    this.errors.email = 'El correo es requerido';
                } else if (this.selectedUser.email.length < 6) {
                    this.errors.email = 'El correo debe tener al menos 6 caracteres';
                } else if (!this.emailRegex.test(this.selectedUser.email)) {
                    this.errors.email = 'El correo no es válido';
                }
            } else if (field === 'password') {
                if (this.selectedUser.password || !this.isEditMode) {
                    if (!this.selectedUser.password && !this.isEditMode) {
                        this.errors.password = 'La contraseña es requerida';
                    } else if (this.selectedUser.password) {
                        const hasUpperCase = /[A-Z]/.test(this.selectedUser.password);
                        const hasLowerCase = /[a-z]/.test(this.selectedUser.password);
                        const hasNumber = /[0-9]/.test(this.selectedUser.password);
                        const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>/\\]/.test(this.selectedUser.password);
                        const hasMinLength = this.selectedUser.password.length >= 8;

                        if (!hasUpperCase || !hasLowerCase || !hasNumber || !hasSpecialChar || !hasMinLength) {
                            this.errors.password = 'La contraseña debe tener al menos una mayúscula, una minúscula, un número, un carácter especial y 8 caracteres en total.';
                        }
                    }
                }
            } else if (field === 'status') {
                if (!this.selectedUser.status) {
                    this.errors.status = 'El estado es requerido';
                }
            } else if (field === 'roleId') {
                if (!this.selectedUser.roleId) {
                    this.errors.roleId = 'El rol es requerido';
                }
            }
        },
        
        validateForm() {
            this.validateField('name');
            this.validateField('email');
            this.validateField('status');
            this.validateField('roleId');
            if (!this.isEditMode) {
                this.validateField('password');
            }
            
            return !Object.values(this.errors).some(error => error !== '');
        },

        async handleSubmit() {
            if (!this.validateForm()) {
                return;
            }
            this.isSendData = true;
            const action = this.isEditMode ? 'actualizar' : 'crear';
            
            try {
                let body = {
                    name: this.selectedUser.name,
                    email: this.selectedUser.email,
                    role_id: this.selectedUser.roleId,
                    status: this.selectedUser.status
                }

                if (this.selectedUser.password || !this.isEditMode) {
                    body.password = this.selectedUser.password;
                }

                let response;
                if (this.isEditMode) {
                    response = await apiService.patchPermission(this.selectedUser.id, body);
                } else {
                    response = await apiService.postPermission(body);
                }

                const currentUser = JSON.parse(localStorage.getItem('user'))?.data?.user;
                const currentUserId = currentUser?._id;
                
                
                if(response) {
                    if (currentUserId === this.selectedUser.id) {
                        const mePermissions = await apiService.getMePermisions();
                        this.$store.commit('setPermissions', mePermissions.data);
                    }
                    this.showSuccessMessage();
                    this.closeModal();
                    this.getPermission();
                } else {
                    await Swal.fire({
                        position: "center",
                        icon: "error",
                        title: `Ocurrio un error al ${action} el permiso.`,
                        showConfirmButton: false,
                        timer: 4500
                    });
                }
                
            
            } catch (error) {
                console.error(`${action} error:`, error);
                await Swal.fire({
                    position: "center",
                    icon: "error",
                    title: `Ocurrio un error.`,
                    showConfirmButton: false,
                    timer: 4500
                });
            } finally {
                this.isSendData = false;
            }
        },
        showSuccessMessage() {
            this.showSuccess = true;
            
            clearTimeout(this.successTimeout);
            this.successTimeout = setTimeout(() => {
                this.showSuccess = false;
            }, 1500); 
        },
        closeSuccessMessage() {
            this.showSuccess = false;
            clearTimeout(this.successTimeout);
        },
        deleteUser(id) {
            this.userToDelete = id;
            if (this.userToDelete) {
                this.$bvModal.show('delete-permission');
            }
        },
        async confirmDelete() {
            if (this.userToDelete) {
                this.isDelete = true;
                try {
                    const response = await apiService.deletePermission(this.userToDelete)
                    if (response) {
                        this.getPermission()
                        this.$bvModal.hide('delete-permission');
                    } else {
                        await Swal.fire({
                            position: "center",
                            icon: "error",
                            title:  'Ocurrio un error al eliminar el permiso.',
                            showConfirmButton: false,
                            timer: 4500
                        });
                    }
                } catch (e) {
                    console.error(e)
                    await Swal.fire({
                        position: "center",
                        icon: "error",
                        title: 'Ocurrio un error al eliminar el permiso.',
                        showConfirmButton: false,
                        timer: 4500
                    });
                } finally {
                    this.isDelete = false;
                }
            }
        },
        cancelDelete() {
            this.$bvModal.hide('delete-permission');
            this.userToDelete = null;
        }
    }
}
</script>

<template>
    <Layout>
        <PageHeader />
        <Loader v-show="loading"></Loader>
        <div class="permissions-container">
            <div class="header">
                <h3>Permisos asignados</h3>
                <button :style="{'display': canCreateUser ? 'block' : 'none'}" class="add-permission" v-if="!showModal" @click="openModal(null, $event)">+ Agregar permiso</button>
            </div>

            <div class="line" v-if="showModal"></div>

            <!-- FORM -->
            <div v-if="showModal" :style="modalStyle" class="modal-container">
                <div class="close">
                    <img src="@/assets/images/close.svg" alt="close" @click="closeModal" />
                </div>
                <form @submit.prevent="handleSubmit">
                    <div class="form-group">
                        <label for="name">Nombre y apellidos</label>
                        <input 
                            type="text" 
                            id="name" 
                            v-model="selectedUser.name" 
                            @input="validateField('name')"
                            @blur="validateField('name')" 
                            placeholder="Nombre y apellidos" 
                            :class="{ 'error-input': errors.name }" 
                        />
                        <span class="error-message" v-if="errors.name">{{ errors.name }}</span>                    
                    </div>
                    <div class="form-group">
                        <label for="email">Email</label>
                        <input 
                            type="email" 
                            id="email" 
                            v-model="selectedUser.email" 
                            @input="validateField('email')"
                            @blur="validateField('email')" 
                            placeholder="ejemplo@gmail.com" 
                            :class="{ 'error-input': errors.email }"
                            :readonly="isAdminUser" 
                        />
                        <span class="error-message" v-if="errors.email">{{ errors.email }}</span>
                    </div>
                    <div class="form-group">
                        <label for="password">Contraseña</label>
                        <div class="password-input-container">
                            <input 
                                :type="showPassword ? 'text' : 'password'" 
                                id="password" 
                                v-model="selectedUser.password" 
                                @input="validateField('password')"
                                @blur="validateField('password')" 
                                placeholder="Contraseña" 
                                :class="{ 'error-input': errors.password }"
                                :readonly="isAdminUser" 
                            />
                            <span @click="showPassword = !showPassword" >
                                <i :class="showPassword ? 'fa fa-eye-slash' : 'fa fa-eye'"></i>
                            </span>
                        </div>
                        <span class="error-message" v-if="errors.password">{{ errors.password }}</span>                   
                     </div>
                    <div class="form-group">
                        <label for="role">Estado</label>
                        <select placeholder="Estado" @change="validateField('status')" @blur="validateField('status')" id="role" v-model="selectedUser.status">
                            <option value="" disabled selected>Estado</option>
                            <option value='Active'>Activo</option>
                            <option value="Pending">Pendiente</option>
                            <option value="Suspended">Suspendido</option>
                        </select>
                        <span class="error-message" v-if="errors.status">{{ errors.status }}</span>                   
                    </div>
                    <div class="form-group-role">
                    <div class="role-options">
                        <div 
                            class="role-option" 
                            v-for="(role, idx) in roles" 
                            :key="role.id"
                        >
                            <input 
                                type="radio" 
                                :id="role.id" 
                                @change="validateField('roleId')"
                                @blur="validateField('roleId')"
                                :value="role.id" 
                                v-model="selectedUser.roleId" 
                            />
                            <div>
                                <label :for="role.id">{{ role.name }}</label>
                                <p>{{ description(idx) }}</p>
                            </div>
                        </div>
                        <span class="error-message" v-if="errors.roleId">{{ errors.roleId }}</span>                   
                    </div>
                </div>
                
                    <div class="action-buttons">
                        <button type="button" @click="closeModal" class="btn-cancel">Cancelar</button>
                        <b-button type="submit" variant="success" class="btn-success save-button" :disabled="isSendData || Object.values(errors).some(error => error && error.length > 0)">
                                Guardar Cambios
                        </b-button>
                    </div>
                </form>

            </div>

            <div class="line"></div>

            <!-- USERS -->
            <div v-if="users">
                <div class="permission-card" v-for="user in users" :key="user.id">
                    <div class="user-info">
                        <h5>{{ user.name }}</h5>
                        <p>{{ user.email }}</p>
                        <p>{{ enumStatus(user.status) }}</p>
                    </div>
                    <div class="actions">
                        <div class="role">
                            <p>{{ user.role_id?.name || 'Sin rol' }}</p>
                        </div>
                        <div :style="{'display': canEditUser ? 'flex' : 'none'}">
                            <img src="@/assets/images/edit.png" alt="edit" @click="openModal(user, $event)" />
                            <img 
                                :style="{'display': canDeleteUser(user) ? 'flex' : 'none'}" 
                                src="@/assets/images/deletePermission.png" 
                                alt="delete" 
                                @click="deleteUser(user._id)" 
                            />                        
                        </div>
                    </div>
                    <div class="line"></div>
                </div>
            </div>

            <div v-else class="no-permissions">
                <p>No hay permisos</p>
            </div>
        </div>

        <div class="success" v-show="showSuccess">
                <div class="user">
                    <img src="@/assets/images/profile-icon.svg" alt="profile icon" />
                    <p>Permiso creado con éxito</p>
                </div>
                <div>
                    <img src="@/assets/images/close.png" alt="close" @click="closeSuccessMessage" />
                </div>
            </div>

        <!-- Delete Modal -->
        <b-modal
            id="delete-permission"
            centered
            hide-footer
            class="delete-modal"
            size="lg"
            >
            <div class="delete-modal">
                <div class="header">
                    <h5>¿Eliminar permiso?</h5>
                    <p>Estás a punto de eliminar el permiso seleccionado. <strong> Esta acción no se puede deshacer y todos los accesos asociados también serán eliminados. ¿Deseas continuar?</strong></p>
                </div>

                <div class="action-buttons">
                <button type="button" class="btn-concel-modal" @click="cancelDelete">Cancelar</button>
                <button type="button" class="btn-success-modal" :style="{ opacity: isDelete ? 0.5 : 1 }" @click="confirmDelete" :disabled="isDelete">Sí, eliminar</button>
            </div>
            </div>
        </b-modal>
    </Layout>
</template>


<style lang="scss" scoped>
@import '@/design/views/permission';
</style>